import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { useAppDispatch, useAppSelector } from './store';
import { setCredentials, selectIsAuthenticated, logout } from './store/slices/authSlice';
import { restoreTimer, stopTimer, selectActiveTimers } from './store/slices/timerSlice';
import { getMe, refreshAccessToken } from './services/api/authService';
import { timeService } from './services/api';
import { queryClient, QUERY_KEYS } from './services/queryClient';

// Layout
import Layout from './shared/components/Layout';
import LoadingScreen from './shared/components/LoadingScreen';
import ProtectedRoute from './shared/components/ProtectedRoute';
import DevLogin from './pages/DevLogin';

// Pages
import DashboardPage from './features/dashboard/pages/DashboardPage';
import TasksPage from './features/tasks/pages/TasksPage';
import DelegatedTasksPage from './features/delegatedTasks/pages/DelegatedTasksPage';
import ProjectsPage from './features/projects/pages/ProjectsPage';
import TicketsPage from './features/tickets/pages/TicketsPage';
import ProjectDetailPage from './features/projects/pages/ProjectDetailPage';
import CalendarPage from './features/calendar/pages/CalendarPage';
import DocumentsPage from './features/documents/pages/DocumentsPage';
import TeamPage from './features/team/pages/TeamPage';
import TeamMemberDetailPage from './features/team/pages/TeamMemberDetailPage';
import ReviewsPage from './features/reviews/pages/ReviewsPage';
import SettingsPage from './features/settings/pages/SettingsPage';
import { DepartmentDashboardPage } from './features/departments';
import UsersPage from './features/users/pages/UsersPage';

// Team Management Components
import KRAKPIManagement from './features/team/components/KRAKPIManagement';
import TeamPerformanceDashboard from './features/team/components/TeamPerformanceDashboard';

// Types for JWT decode
interface DecodedToken {
  sub: string;
  email: string;
  customer_email_id?: string;
  role: string;
  firstName?: string;
  lastName?: string;
  exp: number;
}

function App() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const activeTimers = useAppSelector(selectActiveTimers);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Restore auth from cookie before making any route decision
    const initAuth = () => {
      const token = Cookies.get('authToken');

      if (token) {
        try {
          let decoded: DecodedToken;

          try {
            decoded = jwtDecode<DecodedToken>(token);
          } catch {
            try {
              const decoded_str = atob(token);
              decoded = JSON.parse(decoded_str) as DecodedToken;
            } catch {
              throw new Error('Invalid token format');
            }
          }

          dispatch(stopTimer(undefined));

          if (decoded.exp * 1000 < Date.now()) {
            const refreshToken = Cookies.get('refreshToken');
            if (refreshToken) {
              refreshAccessToken()
                .then((newToken) => {
                  Cookies.set('authToken', newToken, { expires: 1 });
                  return getMe().then((user) => ({ user, token: newToken }));
                })
                .then(({ user, token }) => {
                  dispatch(setCredentials({ user, token }));
                  Cookies.set('userRole', user.role, { expires: 1 });
                  return timeService.getAllActiveTimers();
                })
                .then((activeTimers) => {
                  if (Array.isArray(activeTimers) && activeTimers.length > 0) {
                    activeTimers.forEach(timer => {
                      if (timer.taskId && (timer.status === 'running' || timer.status === 'paused')) {
                        dispatch(restoreTimer(timer));
                      }
                    });
                    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });
                  }
                })
                .catch(() => {
                  Cookies.remove('authToken');
                  Cookies.remove('refreshToken');
                  Cookies.remove('userRole');
                  Cookies.remove('email');
                })
                .finally(() => setAuthChecked(true));
              return;
            }
            Cookies.remove('authToken');
            Cookies.remove('userRole');
            Cookies.remove('email');
            setAuthChecked(true);
            return;
          }

          getMe()
            .then((user) => {
              dispatch(setCredentials({ user, token }));
              Cookies.set('userRole', user.role, { expires: 1 });
              return timeService.getAllActiveTimers();
            })
            .then((activeTimers) => {
              if (Array.isArray(activeTimers) && activeTimers.length > 0) {
                activeTimers.forEach(timer => {
                  if (timer.taskId && (timer.status === 'running' || timer.status === 'paused')) {
                    dispatch(restoreTimer(timer));
                  }
                });
                queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });
              }
            })
            .catch((err) => {
              console.error('Failed to fetch current user:', err);
              Cookies.remove('authToken');
              Cookies.remove('userRole');
              Cookies.remove('email');
            })
            .finally(() => setAuthChecked(true));
          return;
        } catch (error) {
          console.error('Error decoding token:', error);
          Cookies.remove('authToken');
        }
      }
      setAuthChecked(true);
    };

    initAuth();

    const handleUnauthorized = () => {
      dispatch(logout());
      window.location.href = '/login';
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, [dispatch]);

  /**
   * Warn user before closing/refreshing tab if active timers are running
   * 
   * Purpose: Prevents accidental timer loss when user closes browser tab/window
   * 
   * Behavior:
   * - Shows browser's native "leave site?" confirmation dialog
   * - Only triggers if user is authenticated AND has running timers
   * - Paused timers don't trigger warning (already saved to database)
   * 
   * Note: If user proceeds to close tab, timers remain in database and will be
   * automatically restored when user logs back in (handled by initAuth above).
   * 
   * Browser compatibility: Chrome, Firefox, Safari all show their own message,
   * custom message is only used as fallback in older browsers.
   */
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only warn if user is logged in and has running timers
      const runningTimers = activeTimers.filter(t => t.status === 'running');
      if (runningTimers.length > 0 && isAuthenticated) {
        // Prevent default browser behavior
        e.preventDefault();
        // Chrome requires returnValue to be set for dialog to show
        e.returnValue = 'You have active timers running. They will continue running when you return.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [activeTimers, isAuthenticated]);

  if (!authChecked) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      {/* Login - redirect to dashboard if already authenticated */}
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <DevLogin />} />

      {/* Root redirect - send to login if not authenticated, dashboard if authenticated */}
      <Route 
        path="/" 
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
        } 
      />

      {/* Protected routes with layout */}
      <Route
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated}>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard - Landing page */}
        <Route
          path="dashboard"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} pageKey="dashboard">
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Tasks */}
        <Route
          path="tasks"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} pageKey="tasks">
              <TasksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tasks/:view"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} pageKey="tasks">
              <TasksPage />
            </ProtectedRoute>
          }
        />

        {/* Delegated Tasks */}
        <Route
          path="delegated"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} pageKey="delegated">
              <DelegatedTasksPage />
            </ProtectedRoute>
          }
        />

        {/* Projects */}
        <Route
          path="projects"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} pageKey="projects">
              <ProjectsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="projects/:projectId"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} pageKey="projects">
              <ProjectDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tickets"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} pageKey="tickets">
              <TicketsPage />
            </ProtectedRoute>
          }
        />

        {/* Calendar */}
        <Route
          path="calendar"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} pageKey="calendar">
              <CalendarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="documents"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} pageKey="documents">
              <DocumentsPage />
            </ProtectedRoute>
          }
        />

        {/* Team (Manager view) */}
        <Route
          path="team"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['super_admin', 'admin', 'manager']}>
              <TeamPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="team/:memberId"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['super_admin', 'admin', 'manager']}>
              <TeamMemberDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="team/member/:userId"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['super_admin', 'admin', 'manager']}>
              <TeamMemberDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="team/performance"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['super_admin', 'admin', 'manager']}>
              <TeamPerformanceDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="team/kra-kpi"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['super_admin', 'admin', 'manager']}>
              <KRAKPIManagement />
            </ProtectedRoute>
          }
        />

        {/* Departments */}
        <Route
          path="departments"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['super_admin', 'admin', 'manager']}>
              <DepartmentDashboardPage />
            </ProtectedRoute>
          }
        />

        {/* Reviews — all users can view; approvals restricted in page */}
        <Route path="reviews" element={<ReviewsPage />} />

        {/* Users (Admin only) */}
        <Route
          path="users"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['super_admin', 'admin']}>
              <UsersPage />
            </ProtectedRoute>
          }
        />

        {/* Settings — admin and super_admin only */}
        <Route
          path="settings"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated} requiredRoles={['super_admin', 'admin']}>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Redirect any unmatched routes to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
